import { Component, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EEventType, EEventViewMode, TEventFrontendTab } from '@enums/event-tracking.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreatorCampaignExport } from '@pages/campaigns/campaigns.interface';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EPermissions } from '@shared/enums/permissions.enum';
import { CreatorCampaignExportStore } from './creator-campaign-export.store';

type TPendingBriefingAction = 'create' | 'disable' | 'enable';

@Component({
  imports: [DotsSpinnerComponent, HasPermissionDirective, MatButtonModule, MatMenuModule, TranslateModule],
  providers: [CreatorCampaignExportStore],
  selector: 'app-creator-campaign-export',
  templateUrl: './creator-campaign-export.component.html',
})
export class CreatorCampaignExportComponent {
  public readonly campaignId = input.required<string>();
  public readonly creatorId = input.required<string>();
  public readonly creatorName = input.required<string>();
  public readonly creatorCampaignExports = input.required<Map<string, ICreatorCampaignExport>>();
  // Which of the creators tab's view modes (list/tiles) was active - so briefing events reported
  // from here carry that context too.
  public readonly viewMode = input<EEventViewMode | undefined>(undefined);
  // Which campaign-details tab this menu lives on (always the creators tab today) - briefing events
  // report it for downstream context.
  public readonly currentTab = input<TEventFrontendTab | undefined>(undefined);

  public readonly menuTrigger = input<MatMenuTrigger | null>(null);

  public readonly loading = signal<boolean>(false);

  protected readonly EPermissions = EPermissions;

  private readonly creatorCampaignExportStore = inject(CreatorCampaignExportStore);
  private readonly router = inject(Router);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private snackbarMessage = '';
  private pendingAction: TPendingBriefingAction | null = null;

  constructor() {
    effect(() => {
      const loading = this.creatorCampaignExportStore.loading$();
      const creatorCampaignExport = this.creatorCampaignExportStore.creatorCampaignExport$();

      if (loading) {
        setTimeout(() => {
          this.loading.set(loading);
        }, 100);
      } else {
        this.loading.set(false);
      }

      if (!loading && creatorCampaignExport) {
        navigator.clipboard.writeText(this.getBriefingUrl(creatorCampaignExport));
        this.creatorCampaignExports().set(this.creatorId(), creatorCampaignExport);
        this.displaySnackbar();
        this.trackPendingAction(creatorCampaignExport.id);
      }
    });
  }

  public onUpdateBriefing(event: Event): void {
    event?.stopPropagation();
    this.menuTrigger()?.closeMenu();

    const creatorCampaignExport = this.creatorCampaignExports().get(this.creatorId());

    if (!creatorCampaignExport) {
      this.pendingAction = 'create';
      this.creatorCampaignExportStore.createCreatorCampaignExport({
        campaignId: this.campaignId(),
        creatorId: this.creatorId(),
      });

      this.snackbarMessage = this.translate.instant(
        'pages.campaignDetails.table.creatorCampaignExport.successfullyCreated',
        { creatorName: this.creatorName() },
      );

      return;
    }

    if (creatorCampaignExport.isActive) {
      this.pendingAction = 'disable';
      this.creatorCampaignExportStore.updateCreatorCampaignExport({
        id: creatorCampaignExport.id,
        isActive: false,
      });

      this.snackbarMessage = this.translate.instant(
        'pages.campaignDetails.table.creatorCampaignExport.successfullyDisabled',
        { creatorName: this.creatorName() },
      );

      return;
    }

    this.pendingAction = 'enable';
    this.creatorCampaignExportStore.updateCreatorCampaignExport({
      id: creatorCampaignExport.id,
      isActive: true,
    });

    this.snackbarMessage = this.translate.instant(
      'pages.campaignDetails.table.creatorCampaignExport.successfullyEnabled',
      { creatorName: this.creatorName() },
    );
  }

  public onCopyBriefingUrl(event: Event): void {
    event?.stopPropagation();
    this.menuTrigger()?.closeMenu();

    const creatorCampaignExport = this.creatorCampaignExports().get(this.creatorId());

    if (!creatorCampaignExport || !creatorCampaignExport.isActive) {
      return;
    }

    const url = this.getBriefingUrl(creatorCampaignExport);

    navigator.clipboard.writeText(url);

    this.snackbarMessage = this.translate.instant(
      'pages.campaignDetails.table.creatorCampaignExport.successfullyCopiedUrl',
      { creatorName: this.creatorName() },
    );

    this.displaySnackbar();

    this.eventTrackingService.track(EEventType.campaignBriefingUrlCopied, {
      campaignBriefingId: creatorCampaignExport.id,
      campaignId: this.campaignId(),
      creatorId: this.creatorId(),
      viewMode: this.viewMode(),
    });
  }

  private trackPendingAction(campaignBriefingId: string): void {
    const action = this.pendingAction;
    this.pendingAction = null;

    // 'enable' (re-activating a previously disabled briefing) reuses the "created" event rather
    // than getting its own - from an analytics standpoint it's the same "briefing became active"
    // signal as a fresh creation.
    if (action === 'create' || action === 'enable') {
      this.eventTrackingService.track(EEventType.campaignManualBriefingCreated, {
        campaignBriefingId,
        campaignId: this.campaignId(),
        creatorId: this.creatorId(),
        currentTab: this.currentTab(),
        viewMode: this.viewMode(),
      });
    } else if (action === 'disable') {
      this.eventTrackingService.track(EEventType.campaignBriefingDisabled, {
        campaignBriefingId,
        campaignId: this.campaignId(),
        creatorId: this.creatorId(),
        viewMode: this.viewMode(),
      });
    }
  }

  public onOpenBriefing(event: Event): void {
    event?.stopPropagation();
    this.menuTrigger()?.closeMenu();

    const creatorCampaignExport = this.creatorCampaignExports().get(this.creatorId());

    if (!creatorCampaignExport || !creatorCampaignExport.isActive) {
      return;
    }

    WindowRedirectService.openUrlInNewTab(this.getBriefingUrl(creatorCampaignExport, false));
  }

  private displaySnackbar(): void {
    this.snackbarService.openSuccess(this.snackbarMessage);
  }

  // Demo route (campaigns/demo/:id) reuses this exact component tree for its own creators tab -
  // if we're inside that demo tree, the briefing link must point at the demo-mode briefing route
  // (campaigns/demo/briefing/creator/:id, backed by fake services) instead of the real
  // campaigns/briefing/creator/:id, which would otherwise 404 (or worse, hit the real backend)
  // against a fake in-memory id that only the demo fakes know about.
  private getBriefingUrl(creatorCampaignExport: ICreatorCampaignExport, fullUrl = true): string {
    const isDemo = this.router.url.startsWith('/campaigns/demo/');
    const relativePath = isDemo
      ? `/campaigns/demo/briefing/creator/${creatorCampaignExport.id}`
      : `/campaigns/briefing/creator/${creatorCampaignExport.id}`;

    if (fullUrl) {
      return `${WindowRedirectService.getBaseUrl()}${relativePath}`;
    }

    return relativePath;
  }
}
